/**
 * One row of a price comparison: a label (optionally a link), its AZN/m²,
 * a short bar and a "+X% above / -X% below Baku" line. Shared by the
 * district trend pages and the SEO search pages so a comparison reads
 * the same everywhere.
 */
import Link from "@/lib/localized-link";
import type { Locale } from "@/lib/i18n-config";
import { t as translate } from "@/lib/i18n";

function formatPpm(n: number | null | undefined): string {
  if (!n) return "-";
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export default function ComparisonRow({
  label,
  value,
  baku,
  emphasize,
  href,
  locale,
  format = formatPpm,
  unit = "AZN/m²",
}: {
  label: string;
  value: number | null;
  /** Baku-wide median to compare `value` against. `null` when
   *  `value` IS the Baku-wide median (so we skip the delta). */
  baku: number | null;
  /** Bolder typography for "this district" */
  emphasize?: boolean;
  /** If provided, wraps the label in a localized link. */
  href?: string;
  locale: Locale;
  /** Value formatter; rent per m² needs its decimal (11,4). */
  format?: (n: number | null | undefined) => string;
  unit?: string;
}) {
  // Baku delta copy: "+X% above Baku" / "-X% below Baku" / "matches Baku".
  // 0.5% hysteresis mirrors the hero WoW chip.
  let deltaText: string | null = null;
  let deltaClass = "text-gray-500";
  let deltaBar = 0; // 0-100 for visual bar inside comparison row
  if (value != null && baku != null && baku > 0) {
    const pct = ((value - baku) / baku) * 100;
    const abs = Math.abs(pct).toFixed(1);
    if (pct > 0.5) {
      deltaText = translate("district.compare.vsBaku.pricier", locale).replace("{pct}", abs);
      deltaClass = "text-rose-600 dark:text-rose-400";
    } else if (pct < -0.5) {
      deltaText = translate("district.compare.vsBaku.cheaper", locale).replace("{pct}", abs);
      deltaClass = "text-emerald-700 dark:text-emerald-400";
    } else {
      deltaText = translate("district.compare.vsBaku.flat", locale);
    }
    // Map pct to 10-90% range so bars stay visually meaningful
    deltaBar = Math.min(90, Math.max(10, 50 + pct * 1.2));
  }

  const labelEl = href ? (
    <Link href={href} className="text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900">
      {label}
    </Link>
  ) : (
    <span>{label}</span>
  );

  return (
    <div className="px-4 py-3.5 border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className={emphasize ? "font-bold text-gray-900 text-[15px]" : "text-gray-600 text-sm"}>
          {labelEl}
        </div>
        {/* Visual bar - only for rows that have a Baku comparison */}
        {baku != null && value != null && (
          <div className="mt-1.5 h-1 w-full max-w-[160px] rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full ${
                deltaClass.includes("rose") ? "bg-rose-400" :
                deltaClass.includes("emerald") ? "bg-emerald-400" : "bg-gray-300"
              }`}
              style={{ width: `${deltaBar}%` }}
            />
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div
          className={`tabular-nums ${
            emphasize
              ? "text-xl font-extrabold text-gray-900"
              : "text-base font-semibold text-gray-900"
          }`}
        >
          {format(value)}{" "}
          <span className="text-xs text-gray-400 font-normal">{unit}</span>
        </div>
        {deltaText && (
          <div className={`text-[11px] font-medium ${deltaClass} mt-0.5`}>
            {deltaText}
          </div>
        )}
      </div>
    </div>
  );
}
